import React from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Alert } from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  roles?: string[]
  requireTechnicalDept?: boolean
  requireSkillPermission?: 'skill_master_editor' | 'skill_map_viewer'
  requireSalesPermission?: 'manage_accounts' | 'manage_cases' | 'view_cases' | 'view_dashboard' | 'view_kpi'
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles, requireTechnicalDept, requireSkillPermission, requireSalesPermission }) => {
  const { user, skillPermissions, salesPermissions } = useAuth()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.some(role => user.roles.includes(role))

    if (!hasRequiredRole) {
      return (
        <Box p={3}>
          <Alert severity="error">
            このページにアクセスする権限がありません。
            必要な権限: {roles.join(', ')}
          </Alert>
        </Box>
      )
    }
  }

  if (requireTechnicalDept && user.department_type !== 'tech') {
    return (
      <Box p={3}>
        <Alert severity="error">
          この機能は技術部門の社員のみ利用可能です。
        </Alert>
      </Box>
    )
  }

  // Check skill permissions
  if (requireSkillPermission) {
    const hasSkillPermission = skillPermissions.some(
      permission => permission.permission_type === requireSkillPermission
    )

    if (!hasSkillPermission) {
      return (
        <Box p={3}>
          <Alert severity="error">
            このページにアクセスするスキル管理権限がありません。
            必要な権限: {requireSkillPermission === 'skill_master_editor' ? 'スキルマスタ編集' : 'スキルマップ閲覧'}
          </Alert>
        </Box>
      )
    }
  }

  // Check sales permissions
  if (requireSalesPermission) {
    if (!salesPermissions) {
      return (
        <Box p={3}>
          <Alert severity="error">
            このページにアクセスする営業管理権限がありません。
          </Alert>
        </Box>
      )
    }

    let hasSalesPermission = false
    switch (requireSalesPermission) {
      case 'manage_accounts':
        hasSalesPermission = salesPermissions.can_manage_accounts
        break
      case 'manage_cases':
        hasSalesPermission = salesPermissions.can_manage_cases
        break
      case 'view_cases':
        hasSalesPermission = salesPermissions.can_view_cases || salesPermissions.can_manage_cases
        break
      case 'view_dashboard':
        hasSalesPermission = salesPermissions.can_view_dashboard
        break
      case 'view_kpi':
        hasSalesPermission = salesPermissions.can_view_kpi
        break
    }

    if (!hasSalesPermission) {
      const permissionNames: { [key: string]: string } = {
        manage_accounts: '取引先管理',
        manage_cases: '案件管理',
        view_cases: '案件閲覧',
        view_dashboard: 'ダッシュボード閲覧',
        view_kpi: 'KPI閲覧'
      }

      return (
        <Box p={3}>
          <Alert severity="error">
            このページにアクセスする営業管理権限がありません。
            必要な権限: {permissionNames[requireSalesPermission]}
          </Alert>
        </Box>
      )
    }
  }

  return <>{children}</>
}

export default ProtectedRoute